/**
 * remark plugin that normalizes AI-authored timestamp mentions in a
 * markdown AST into a uniform inline marker that AgentThreadMarkdownLink renders
 * via the shared `Timestamp` component, formatted according to the user's
 * TimestampFormat preference.
 *
 * Three surface forms are rewritten:
 *
 *   1. Bare prose:             `… at t=1771481251468476035ns …`
 *   2. Fully-highlighted:      ``` `t=1771481251468476035ns` ```
 *   3. Split highlight:        ``` t=`1771481251468476035ns` ```
 *
 * (1) → `ai-ts:<ns>` (plain). (2) and (3) → `ai-ts-hl:<ns>` (highlighted —
 * the renderer wraps it in `<code>` so the inline-code look the AI
 * intended is preserved).
 *
 * Existing link nodes (`timestamp:` / `roboto://`) and fenced code blocks
 * are skipped — their content is already authored by the model or user
 * verbatim and should pass through unchanged.
 *
 * Detection lives in ./timestampMentionDetection — this file is just the
 * AST walker.
 */

import {
  findTimestampMentions,
  matchBareUnitMention,
  stripTrailingTEquals,
} from "./timestampMentionDetection";

// Re-exported for backwards compatibility with consumers (and tests) that
// imported these directly from this module.
export {
  findTimestampMentions,
  isTimestampMention,
} from "./timestampMentionDetection";
export { isLikelyNumericTimestampLabel } from "@/shared/components/RobotoMarkdown/detection";

interface MdastNode {
  type: string;
  value?: string;
  url?: string;
  children?: MdastNode[];
}

function rewriteTextNode(
  node: MdastNode,
  index: number,
  parent: MdastNode,
): number {
  const value = node.value ?? "";
  const mentions = findTimestampMentions(value);
  if (mentions.length === 0) {
    return index + 1;
  }

  const replacements: MdastNode[] = [];
  let cursor = 0;
  for (const m of mentions) {
    if (m.start > cursor) {
      replacements.push({ type: "text", value: value.slice(cursor, m.start) });
    }
    replacements.push({
      type: "link",
      url: `ai-ts:${m.ns.toString()}`,
      children: [{ type: "text", value: m.raw }],
    });
    cursor = m.end;
  }
  if (cursor < value.length) {
    replacements.push({ type: "text", value: value.slice(cursor) });
  }

  parent.children!.splice(index, 1, ...replacements);
  return index + replacements.length;
}

/**
 * Rewrite an inlineCode node carrying an AI-authored timestamp mention.
 *
 * Two shapes handled:
 *   - Whole mention inside the code span (`` `t=<n><unit>` ``).
 *   - Only the digits+unit half inside the code span, with `t=` sitting
 *     in the preceding text sibling (``… t=`<n><unit>` …``). The trailing
 *     `t=` is consumed from the previous text node.
 *
 * Both collapse to a single `ai-ts-hl:<ns>` marker.
 */
function rewriteInlineCodeNode(index: number, parent: MdastNode): number {
  const children = parent.children!;
  const node = children[index];
  const value = node.value ?? "";

  const full = findTimestampMentions(value);
  if (
    full.length === 1 &&
    full[0].start === 0 &&
    full[0].end === value.length
  ) {
    children[index] = {
      type: "link",
      url: `ai-ts-hl:${full[0].ns.toString()}`,
      children: [{ type: "text", value: full[0].raw }],
    };
    return index + 1;
  }

  const bare = matchBareUnitMention(value);
  if (bare && index > 0) {
    const prev = children[index - 1];
    if (prev.type === "text" && prev.value !== undefined) {
      const kept = stripTrailingTEquals(prev.value);
      if (kept !== null) {
        prev.value = kept;
        children[index] = {
          type: "link",
          url: `ai-ts-hl:${bare.ns.toString()}`,
          children: [{ type: "text", value: bare.raw.trim() }],
        };
        return index + 1;
      }
    }
  }

  return index + 1;
}

// Bespoke walker rather than `unist-util-visit`: this transformer needs to
// SPLICE replacement nodes into the parent's `children` array (a single text
// node may expand into [text, link, text, link, ...]) and skip subtrees of
// `link`/`code` parents at the visitor level. `unist-util-visit` is built
// for read-only traversal, so adopting it would still require a hand-rolled
// splice loop — the simpler approach is to inline both. Position info is
// intentionally dropped on synthesized nodes; the AST is consumed downstream
// only by the markdown renderer, not by source-map-aware tooling.
function walk(node: MdastNode): void {
  // Skip descending into links and fenced code blocks. Inline code is
  // walked so rewriteInlineCodeNode can upgrade highlighted mentions.
  if (node.type === "link" || node.type === "code") {
    return;
  }
  if (!node.children) {
    return;
  }

  let i = 0;
  while (i < node.children.length) {
    const child = node.children[i];
    if (child.type === "text") {
      i = rewriteTextNode(child, i, node);
      continue;
    }
    if (child.type === "inlineCode") {
      i = rewriteInlineCodeNode(i, node);
      continue;
    }
    walk(child);
    i += 1;
  }
}

export function remarkTimestampMentions() {
  // Conventional unified plugin signature: `(tree, file?) => void`. The
  // `file` parameter is unused today but accepted so other plugins (and
  // future tooling that introspects plugin shape) see the standard arity.
  return (tree: MdastNode, _file?: unknown) => {
    walk(tree);
  };
}
